import { useEffect, useMemo, useState } from 'react'
import { Button, Collapse, Empty, Popconfirm, Space, Spin, Tag, Tooltip, Typography } from 'antd'
import { DeleteOutlined, PlusOutlined, ReloadOutlined, UndoOutlined, UserAddOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import type { TaskItem } from '../../services/tasksApi'
import { useAuthStore } from '../../store/authStore'
import {
    CLOSED, DATE_FORMAT, DoneIcon, EditIcon, PRIORITY_LABELS, STATUS_LABELS, useTaskEditor, type FixedLead,
} from './taskShared'

const { Text } = Typography

// Вкладка «Задачи» в карточке лида: те же задачи и те же действия, что в
// разделе «Задачи», только по одному лиду. Видимость задач - как там же:
// оператор видит свои, руководитель - и задачи младших.

type Props = {
    lead: FixedLead
    tasks: TaskItem[]
    loading: boolean
    onReload: () => void | Promise<void>
}

export default function LeadTasksTab({ lead, tasks, loading, onReload }: Props) {
    const { openCreate, openEdit, requestClose, setStatus, remove, take, modals } = useTaskEditor({ onChanged: onReload, fixedLead: lead })
    const { user } = useAuthStore()

    // Время освежается раз в полминуты: задача сама становится просроченной.
    const [now, setNow] = useState(() => dayjs())
    useEffect(() => {
        const timer = window.setInterval(() => setNow(dayjs()), 30000)
        return () => window.clearInterval(timer)
    }, [])

    const open = useMemo(() => tasks.filter((task) => !CLOSED.includes(task.status)), [tasks])
    const closed = useMemo(() => tasks
        .filter((task) => CLOSED.includes(task.status))
        .sort((a, b) => dayjs(b.completed_at || b.updated_at).valueOf() - dayjs(a.completed_at || a.updated_at).valueOf()), [tasks])

    const renderTask = (task: TaskItem) => {
        const isClosed = CLOSED.includes(task.status)
        const overdue = !isClosed && Boolean(task.deadline) && dayjs(task.deadline).isBefore(now)
        const priority = PRIORITY_LABELS[task.priority] || PRIORITY_LABELS.medium
        const status = STATUS_LABELS[task.status] || STATUS_LABELS.pending
        return (
            <div key={task.id} className={`lead-task-item${overdue ? ' is-overdue' : ''}`}>
                <div className="lead-task-main">
                    <Space size={6} wrap>
                        <Text strong={!isClosed}>{task.title}</Text>
                        {/* Откуда задача: поставлена в CRM Wiseway или у нас. */}
                        {task.crm_origin === 'imported' ? (
                            <Tag color="purple">Из CRM WW #{task.crm_task_id}</Tag>
                        ) : task.crm_task_id ? (
                            <Tooltip title="Поставлена в Оркестраторе, копия — в CRM Wiseway">
                                <Tag color="blue">Оркестратор · CRM #{task.crm_task_id}</Tag>
                            </Tooltip>
                        ) : (
                            <Tag>Оркестратор</Tag>
                        )}
                    </Space>
                    {task.description && <Text type="secondary" style={{ fontSize: 12 }}>{task.description}</Text>}
                    {task.close_comment && (
                        <Text type="secondary" style={{ fontSize: 12 }}>Закрыта с комментарием: {task.close_comment}</Text>
                    )}
                    <Space size={6} wrap className="lead-task-meta">
                        {task.deadline ? (
                            <Text type={overdue ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
                                Срок {dayjs(task.deadline).format(DATE_FORMAT)}{overdue ? ' · просрочена' : ''}
                            </Text>
                        ) : <Text type="secondary" style={{ fontSize: 12 }}>без срока</Text>}
                        {isClosed && task.completed_at && (
                            <Text type="secondary" style={{ fontSize: 12 }}>· закрыта {dayjs(task.completed_at).format(DATE_FORMAT)}</Text>
                        )}
                        {task.visibility_profile_id && <Tag color="geekblue">Ответственный: РП «{task.visibility_profile_name}»</Tag>}
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            · {task.assigned_to_name
                                ? (task.visibility_profile_id ? `ведёт ${task.assigned_to_name}` : task.assigned_to_name)
                                : (task.visibility_profile_id ? 'возьмёт любой из РП' : 'не назначен')}
                        </Text>
                        <Tag color={priority.color}>{priority.label}</Tag>
                        <Tag color={status.color}>{status.label}</Tag>
                    </Space>
                </div>
                <Space size={2} className="lead-task-actions">
                    {task.visibility_profile_id && task.assigned_to_id !== user?.id && !isClosed && (
                        <Tooltip title="Взять себе">
                            <Button type="text" icon={<UserAddOutlined />} onClick={() => take(task)} />
                        </Tooltip>
                    )}
                    {!isClosed ? (
                        <Tooltip title="Отметить выполненной">
                            <Button type="text" className="task-icon-btn" icon={<DoneIcon />} onClick={() => requestClose(task)} />
                        </Tooltip>
                    ) : (
                        <Tooltip title="Вернуть в работу">
                            <Button type="text" icon={<UndoOutlined />} onClick={() => setStatus(task, 'pending')} />
                        </Tooltip>
                    )}
                    <Tooltip title="Изменить">
                        <Button type="text" className="task-icon-btn" icon={<EditIcon />} onClick={() => openEdit(task)} />
                    </Tooltip>
                    {/* Слева от кнопки: у правого края карточки подтверждение вверху
                        перекрывалось подсказкой «Удалить». */}
                    <Popconfirm placement="left" title="Удалить задачу?" okText="Удалить" cancelText="Отмена" onConfirm={() => remove(task)}>
                        <Tooltip title="Удалить">
                            <Button type="text" danger icon={<DeleteOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            </div>
        )
    }

    return (
        <Spin spinning={loading}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Space style={{ width: '100%', justifyContent: 'space-between' }} align="center">
                    <Text type="secondary">
                        {open.length ? `Активных: ${open.length}` : 'Активных задач нет'}
                    </Text>
                    <Space size={8}>
                        <Button icon={<ReloadOutlined />} onClick={() => void onReload()} />
                        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Задача</Button>
                    </Space>
                </Space>

                {open.length ? (
                    <div className="lead-task-list">{open.map(renderTask)}</div>
                ) : (
                    <Empty description="Задач по лиду нет" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}

                {closed.length > 0 && (
                    <Collapse
                        ghost
                        items={[{
                            key: 'closed',
                            label: `Закрытые (${closed.length})`,
                            children: <div className="lead-task-list">{closed.map(renderTask)}</div>,
                        }]}
                    />
                )}
            </Space>
            {modals}
        </Spin>
    )
}
